import { worldDistance3D, type WorldPos } from './measureUtils';
import * as THREE from 'three';

const NAV_POSE_INDEX_URL = '/nav-poses/terrebonne_nav_pose_index.json';

export interface NavPose {
  id: number | string;
  name?: string;
  worldPos: WorldPos;
}

interface NavPoseIndexResponse {
  poses?: Array<{
    id?: number | string;
    name?: string;
    worldPos?: [number, number, number];
  }>;
}

export interface NavPoseMatchDebug {
  nearest: NavPose | null;
  nearestDistance: number;
  topCandidates: Array<{
    id: number | string;
    name?: string;
    distance: number;
    worldPos: WorldPos;
  }>;
}

let navPoseCache: NavPose[] | null = null;
let navPoseLoadPromise: Promise<NavPose[]> | null = null;

export async function loadNavPoseIndex(): Promise<NavPose[]> {
  if (navPoseCache) return navPoseCache;
  if (navPoseLoadPromise) return navPoseLoadPromise;

  navPoseLoadPromise = fetch(NAV_POSE_INDEX_URL)
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`Failed to load nav pose index: ${response.status}`);
      }

      const data = (await response.json()) as NavPoseIndexResponse;
      const poses = Array.isArray(data.poses)
        ? data.poses.flatMap((pose) => {
            const worldPos = pose.worldPos;
            const poseId = pose.id;
            if (
              !worldPos ||
              worldPos.length < 3 ||
              !Number.isFinite(worldPos[0]) ||
              !Number.isFinite(worldPos[1]) ||
              !Number.isFinite(worldPos[2]) ||
              (typeof poseId !== 'number' && typeof poseId !== 'string')
            ) {
              return [];
            }
            const raw = new THREE.Vector3(worldPos[0], worldPos[1], worldPos[2]);
            raw.applyQuaternion(new THREE.Quaternion(0.5, 0.5, 0.5, -0.5));

            return [{
              id: poseId,
              name: pose.name,
              worldPos: {
                x: raw.x,
                y: raw.y,
                z: raw.z,
              },
            }];
          })
        : [];

      if (poses.length === 0) {
        throw new Error('Nav pose index is empty');
      }

      navPoseCache = poses;
      return poses;
    })
    .catch((error) => {
      navPoseLoadPromise = null;
      throw error;
    });

  return navPoseLoadPromise;
}

export async function findNearestNavPose(target: WorldPos): Promise<NavPose | null> {
  const result = await findNearestNavPoseWithDebug(target);
  return result.nearest;
}

export async function findNearestNavPoseWithDebug(target: WorldPos): Promise<NavPoseMatchDebug> {
  const poses = await loadNavPoseIndex();

  let nearestPose: NavPose | null = null;
  let nearestDistance = Infinity;

  const candidates: Array<{
    id: number | string;
    name?: string;
    distance: number;
    worldPos: WorldPos;
  }> = [];

  for (const pose of poses) {
    const distance = worldDistance3D(target, pose.worldPos);

    candidates.push({
      id: pose.id,
      name: pose.name,
      distance,
      worldPos: pose.worldPos,
    });

    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestPose = pose;
    }
  }

  candidates.sort((a, b) => a.distance - b.distance);

  return {
    nearest: nearestPose,
    nearestDistance,
    topCandidates: candidates.slice(0, 3),
  };
}